import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export const useViewCount = (blogPostId: string) => {
  const { user } = useAuth();
  const [viewCount, setViewCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchViewCount = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_blog_post_view_count', { post_id: blogPostId });

      if (error) throw error;

      setViewCount(data || 0);
    } catch (error: any) {
      console.error('Error fetching view count:', error);
    } finally {
      setLoading(false);
    }
  };

  const trackView = async (sessionKey?: string) => {
    // Only track if we have either user_id or session_key (RLS requirement)
    if (!user?.id && !sessionKey) {
      console.log('Skipping view tracking - no user_id or session_key');
      return;
    }

    try {
      const { error } = await supabase
        .from('views')
        .insert({
          blog_post_id: blogPostId,
          user_id: user?.id || null,
          session_key: sessionKey || null,
          ip_address: null,
          user_agent: navigator.userAgent,
          association_status: 'pending'
        });

      if (error) throw error;
      
      // Refresh the count after tracking a view
      fetchViewCount();
    } catch (error: any) {
      console.error('Error tracking view:', error);
    }
  };

  useEffect(() => {
    fetchViewCount();
  }, [blogPostId]);

  // Subscribe to real-time changes for this blog post's views
  useEffect(() => {
    let pollInterval: NodeJS.Timeout | null = null;
    
    const channel = supabase
      .channel('view-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'views',
          filter: `blog_post_id=eq.${blogPostId}`
        },
        () => {
          fetchViewCount();
        }
      )
      .subscribe((status) => {
        // Silently handle connection errors and fallback to polling
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          // Fallback to polling every 30 seconds if realtime fails
          if (!pollInterval) {
            pollInterval = setInterval(fetchViewCount, 30000);
          }
        }
      });

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      supabase.removeChannel(channel);
    };
  }, [blogPostId]);

  return {
    viewCount,
    loading,
    trackView,
    refetch: fetchViewCount
  };
};